import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');
    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');
    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#ensureDrawerExists();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #ensureDrawerExists() {
    if (isMobileBreakpoint()) return;
    if (document.getElementById('accordion-side-drawer')) return;

    const overlay = document.createElement('div');
    overlay.id = 'accordion-side-overlay';

    const drawer = document.createElement('div');
    drawer.id = 'accordion-side-drawer';
    drawer.innerHTML = `
      <div id="accordion-drawer-header">
        <span id="accordion-drawer-title"></span>
        <button id="accordion-drawer-close" aria-label="Close">✕</button>
      </div>
      <div id="accordion-drawer-body"></div>
    `;

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    const close = () => {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
    };

    overlay.addEventListener('click', close);
    document.getElementById('accordion-drawer-close').addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // 手机端保持原始展开行为
    if (isMobile) return;

    // 桌面端：拦截默认展开，改为右侧抽屉
    event.preventDefault();

    let title = '';
    this.summary.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) title = text;
      }
    });
    if (!title) title = this.summary.textContent.trim();

    const contentEl = this.details.querySelector('.details-content');
    const body = contentEl ? contentEl.innerHTML : '';

    document.getElementById('accordion-drawer-title').textContent = title;
    document.getElementById('accordion-drawer-body').innerHTML = body;

    const drawer = document.getElementById('accordion-side-drawer');
    const overlay = document.getElementById('accordion-side-overlay');
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
  };

  #handleContentClick = (event) => {
    if (!this.details.open) return;
    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;
    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#ensureDrawerExists();
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
